import { useState } from "react";
import styled from "@emotion/styled";
import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextOverflow,
  MDSTextVariant,
} from "@/mds";
import type { FC, ReactNode } from "react";

export interface CollapsibleSectionHeaderProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> {
  title?: string | null;
  titleAccessory?: ReactNode;
  headerExtra?: ReactNode;
  children: ReactNode;
  defaultCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
}

export const CollapsibleSectionHeader: FC<CollapsibleSectionHeaderProps> = ({
  title,
  titleAccessory,
  headerExtra,
  children,
  defaultCollapsed = false,
  onCollapseChange,
  className,
  ...props
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isHovered, setIsHovered] = useState(false);

  const sectionTitle = title ?? "Section";

  const toggle = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    onCollapseChange?.(next);
  };

  return (
    <>
      <SectionHeaderContainer
        {...props}
        className={className}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={toggle}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
      >
        <SectionHeaderButton $isHovered={isHovered}>
          <SectionHeaderIcon $isCollapsed={isCollapsed}>
            <MDSIcon
              kind={isCollapsed ? MDSIconKind.CaretRight : MDSIconKind.CaretDown}
              color={MDSIconColor.Secondary}
              size={11}
            />
          </SectionHeaderIcon>
        </SectionHeaderButton>
        <SectionHeaderTitleGroup>
          <SectionHeaderTitle variant={MDSTextVariant.TextXs} overflow={MDSTextOverflow.Regular}>
            {sectionTitle}
          </SectionHeaderTitle>
          {titleAccessory ? (
            <SectionHeaderTitleAccessory>{titleAccessory}</SectionHeaderTitleAccessory>
          ) : null}
        </SectionHeaderTitleGroup>
        <SectionHeaderSpacer />
        {headerExtra}
      </SectionHeaderContainer>
      {!isCollapsed && children}
    </>
  );
};

const SectionHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: ${({ theme }) => theme.spacing["2"]};
  padding-bottom: ${({ theme }) => theme.spacing["2"]};
  padding-left: ${({ theme }) => theme.spacing["2"]};
  padding-right: ${({ theme }) => theme.spacing["2"]};
  min-height: 32px;
  align-self: stretch;
  width: 100%;
  cursor: pointer;
`;

const SectionHeaderButton = styled.button<{ $isHovered: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: ${({ theme, $isHovered }) =>
    $isHovered
      ? theme.colors.background.button.transparent.hover
      : theme.colors.background.button.transparent.default};
  border: none;
  cursor: pointer;
  border-radius: 100px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }

  & svg {
    margin-top: -1px;
  }
`;

const SectionHeaderIcon = styled.div<{ $isCollapsed: boolean }>`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 150ms ease;
`;

const SectionHeaderTitleGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
`;

const SectionHeaderTitle = styled(MDSText)`
  text-transform: uppercase;
  letter-spacing: 0.24px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  display: inline-flex;
  flex: 0 1 auto;
  text-align: left;
  min-width: 0;
  max-width: 100%;
  width: auto;
`;

const SectionHeaderTitleAccessory = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const SectionHeaderSpacer = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;
